import { IProduct, Measures } from "../types/entities";
import { Date } from "./Date";
import { v4 as uuid } from "uuid";
import { action, computed, makeObservable, observable } from "mobx";
import { Box } from "./Box";
import { resolveBoxById } from "./relations";

function normalizeProductPhotos(input: unknown): string[] {
  if (!Array.isArray(input)) {
    return [];
  }

  const unique = new Set<string>();
  const normalized: string[] = [];

  input.forEach((item) => {
    if (typeof item !== "string") {
      return;
    }

    const value = item.trim();
    if (!value || unique.has(value)) {
      return;
    }

    unique.add(value);
    normalized.push(value);
  });

  return normalized;
}

function mergePrimaryPhoto(primary: string, photos: string[]): string[] {
  const normalizedPrimary = primary.trim();
  const merged = [...photos];

  if (normalizedPrimary && !merged.includes(normalizedPrimary)) {
    merged.unshift(normalizedPrimary);
  }

  if (!normalizedPrimary && merged.length > 0) {
    return merged;
  }

  return merged;
}

export class Product implements IProduct {
  constructor(product?: IProduct) {
    makeObservable(this);

    if (product) {
      this.id = product.id || uuid();
      this.name = product.name;
      const normalizedPhotos = mergePrimaryPhoto(
        product.photo || "",
        normalizeProductPhotos(product.photos),
      );
      this.photo = normalizedPhotos[0] || product.photo || "";
      this.photos = normalizedPhotos;
      this.description = product.description;
      this.manufacturingDate = product.manufacturingDate
        ? new Date(product.manufacturingDate)
        : null;
      this.expiryDate = product.expiryDate
        ? new Date(product.expiryDate)
        : null;
      this.typeTags = Array.isArray(product.typeTags) ? product.typeTags : [];
      this.measure = product.measure;
      this.quantity = product.quantity;
      this.parentId = product.parentId;
    }
  }

  @observable
  id: string = uuid();

  @observable
  name: string = "";

  @observable
  photo: string = "";

  @observable.shallow
  photos: string[] = [];

  @observable
  description: string = "";

  @observable
  manufacturingDate: Date | null = null;

  @observable
  expiryDate: Date | null = null;

  @observable.shallow
  typeTags: string[] = [];

  @observable
  measure: Measures = Measures.weight;

  @observable
  quantity: number = 1;

  @observable
  parentId: string | null = null;

  @computed
  get parent(): Box | undefined {
    if (this.parentId) {
      return resolveBoxById(this.parentId);
    }
    return undefined;
  }

  @action
  setName = (_: unknown, { value }: { value: string }) => {
    this.name = value;
  };

  @action
  setDescription = (_: unknown, { value }: { value: string }) => {
    this.description = value;
  };

  toJSON = () => ({
    id: this.id,
    name: this.name,
    photo: this.photo,
    photos: this.photos,
    description: this.description,
    manufacturingDate: this.manufacturingDate,
    expiryDate: this.expiryDate,
    typeTags: this.typeTags,
    measure: this.measure,
    quantity: this.quantity,
    parentId: this.parentId,
  });
}
